import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { Box } from '@mui/material';
import { useIsLoggedIn } from 'features/auth/hooks/useAuthData';
import { useCobrandedPath } from 'features/cobrand/hooks/useCobrandUtils';
import { AppLinkConfig } from 'features/appLinks/types';
import { useAppLinkParams } from 'features/appLinks/useAppLinkParams';

interface AppLinkGuardProps {
  /**
   * Configuration for this app link route.
   * Defines auth requirements, required/optional parameters, and error handling.
   */
  config: AppLinkConfig;

  /**
   * Handler component that implements feature-specific business logic.
   * The handler is responsible for its own loading/error states and rendering.
   */
  children: React.ReactNode;
}

/**
 * Generic guard component for handling app links (email CTAs, SMS links, callbacks, etc.).
 *
 * Responsibilities:
 * - Auth preservation: Redirects unauthenticated users to login with returnUrl
 * - Param validation: Validates required/optional params from URL
 * - Error handling: Shows error screen or redirects on validation failure
 * - Loading state: Shows loading spinner during handler processing
 * - Pass-through: Renders children when all checks pass
 *
 * @example
 * // In Routes.tsx:
 * <Route
 *   path="enrollment"
 *   element={
 *     <AppLinkGuard
 *       config={{
 *         requiresAuth: true,
 *         requiredParams: ['enrollmentId'],
 *         redirectOnMissingParams: '/home'
 *       }}
 *     >
 *       <Enrollment />
 *     </AppLinkGuard>
 *   }
 * />
 */
export const AppLinkGuard: React.FC<AppLinkGuardProps> = ({ config, children }) => {
  const isLoggedIn = useIsLoggedIn();
  const location = useLocation();
  const loginPath = useCobrandedPath('/login');
  const redirectPath = useCobrandedPath(config.redirectOnMissingParams || '/home');
  const { isValid, error: validationError, missingParams } = useAppLinkParams(config);

  // Step 1: Auth Check
  // If auth is required and user is not logged in, redirect to login
  // Preserve the current URL (including all params) via state.returnUrl
  if (config.requiresAuth && !isLoggedIn) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={loginPath} replace state={{ returnUrl }} />;
  }

  // Step 2: Param Validation
  // If required params are missing, either redirect or show error
  if (!isValid) {
    // Option A: Redirect to specified path
    if (config.redirectOnMissingParams) {
      return <Navigate to={redirectPath} replace />;
    }

    // Option B: Show error screen inline
    return (
      <Box
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100vw"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <Box sx={{ textAlign: 'center', color: 'error.main', fontSize: '1.25rem' }}>
          Invalid Link
        </Box>
        <Box sx={{ textAlign: 'center', color: 'gray.600', fontSize: '0.875rem' }}>
          {validationError}
        </Box>
        {missingParams.length > 0 && (
          <Box sx={{ textAlign: 'center', color: 'gray.500', fontSize: '0.75rem', mt: 1 }}>
            Missing parameters: {missingParams.join(', ')}
          </Box>
        )}
      </Box>
    );
  }

  // Step 3: Pass Through
  // All checks passed, render the handler
  // Handler is responsible for its own loading/error states
  return <>{children}</>;
};
